import { useEffect, useState } from 'react';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import type { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';

// One-time worker registration. Idempotent — pdfjs sets it on first read.
if (!GlobalWorkerOptions.workerSrc) {
  GlobalWorkerOptions.workerSrc = workerUrl;
}

interface PdfPageMetadata {
  /** 1-indexed, matches pdfjs page numbering. */
  pageNumber: number;
  /** Intrinsic width at scale=1, post-rotation. */
  width: number;
  /** Intrinsic height at scale=1, post-rotation. */
  height: number;
}

export interface PdfDocumentState {
  pdf: PDFDocumentProxy | null;
  pages: PdfPageMetadata[];
  loading: boolean;
  /**
   * - 'load': pdfjs failed to load/parse the document.
   * - 'password': document is encrypted and we don't support password entry yet.
   * - null: no error.
   */
  error: 'load' | 'password' | null;
}

/**
 * Module-level LRU cache of resolved {pdf, pages} pairs, keyed by the asset's
 * stable URL path (signed-URL query params stripped — the workspace-api
 * re-signs the asset URL on every /detail call, so the full `downloadUrl`
 * changes between mounts even for the same file). Lets re-opens of a
 * previously-viewed PDF (Library preview navigation) render the page
 * scaffolding synchronously instead of going through pdfjs's fetch + parse
 * spinner on every entry.
 *
 * Capped at 8 entries (each pdf proxy holds a worker + parsed document, so
 * the per-entry footprint is heavier than text bodies). LRU eviction destroys
 * the evicted pdf so its worker is freed instead of leaking for the rest of
 * the session.
 */
const PDF_CACHE_MAX = 8;
const pdfCache = new Map<string, { pdf: PDFDocumentProxy; pages: PdfPageMetadata[] }>();

function pdfCacheKey(downloadUrl: string): string {
  const queryIndex = downloadUrl.indexOf('?');
  return queryIndex === -1 ? downloadUrl : downloadUrl.slice(0, queryIndex);
}

function readPdfCache(
  key: string,
): { pdf: PDFDocumentProxy; pages: PdfPageMetadata[] } | undefined {
  const hit = pdfCache.get(key);
  if (!hit) return undefined;
  // Refresh recency: re-insert as the most-recent entry.
  pdfCache.delete(key);
  pdfCache.set(key, hit);
  return hit;
}

function writePdfCache(
  key: string,
  value: { pdf: PDFDocumentProxy; pages: PdfPageMetadata[] },
): void {
  if (pdfCache.has(key)) pdfCache.delete(key);
  pdfCache.set(key, value);
  while (pdfCache.size > PDF_CACHE_MAX) {
    const oldestKey = pdfCache.keys().next().value;
    if (oldestKey === undefined) break;
    const oldest = pdfCache.get(oldestKey);
    pdfCache.delete(oldestKey);
    oldest?.pdf.destroy().catch(() => {});
  }
}

/**
 * Loads a PDF document and extracts intrinsic page sizes upfront. The page
 * sizes are cheap to compute and let the viewer reserve correct vertical
 * space before any canvas/text-layer renders.
 */
export function usePdfDocument(downloadUrl: string | null | undefined): PdfDocumentState {
  const [state, setState] = useState<PdfDocumentState>(() => {
    if (!downloadUrl) return { pdf: null, pages: [], loading: false, error: null };
    const cached = readPdfCache(pdfCacheKey(downloadUrl));
    if (cached) return { pdf: cached.pdf, pages: cached.pages, loading: false, error: null };
    return { pdf: null, pages: [], loading: true, error: null };
  });

  useEffect(() => {
    if (!downloadUrl) {
      setState({ pdf: null, pages: [], loading: false, error: null });
      return;
    }

    const key = pdfCacheKey(downloadUrl);
    const cached = readPdfCache(key);
    if (cached) {
      // `useState` initializer already seeded state from the cache. No fetch,
      // no parse — and crucially, no cleanup-time destroy: the cached pdf
      // proxy must outlive this mount so the next open hits the cache too.
      setState({ pdf: cached.pdf, pages: cached.pages, loading: false, error: null });
      return;
    }

    const ac = new AbortController();
    const aborted = () => ac.signal.aborted;
    /** `task.destroy()` tears down the resolved pdf's worker; skip it once the result is cached. */
    let didCache = false;
    setState({ pdf: null, pages: [], loading: true, error: null });

    const task = getDocument({ url: downloadUrl });
    task.onPassword = () => {
      if (aborted()) return;
      setState({ pdf: null, pages: [], loading: false, error: 'password' });
      task.destroy().catch(() => {});
    };

    task.promise
      .then(async (pdf) => {
        if (aborted()) {
          await pdf.destroy().catch(() => {});
          return;
        }
        const pages = await Promise.all(
          Array.from({ length: pdf.numPages }, async (_, i) => {
            const pageNumber = i + 1;
            const page = await pdf.getPage(pageNumber);
            const viewport = page.getViewport({ scale: 1 });
            return { pageNumber, width: viewport.width, height: viewport.height };
          }),
        );
        if (aborted()) {
          await pdf.destroy().catch(() => {});
          return;
        }
        writePdfCache(key, { pdf, pages });
        didCache = true;
        setState({ pdf, pages, loading: false, error: null });
      })
      .catch(() => {
        if (aborted()) return;
        setState({ pdf: null, pages: [], loading: false, error: 'load' });
      });

    return () => {
      ac.abort();
      if (didCache) return;
      task.destroy().catch(() => {});
      setState((prev) => {
        if (prev.pdf) prev.pdf.destroy().catch(() => {});
        return prev;
      });
    };
  }, [downloadUrl]);

  return state;
}
